import { useQuery } from "@rocicorp/zero/react";
import { Fragment } from "react";
import { queries } from "../../../../shared/queries";
import { displaySpaceName } from "../../../../shared/spaceName";
import { spaceIcon } from "../../../lib/spaceIcon";
import { userDisplayName } from "../../../lib/userDisplayName";
import { Icon } from "../../Icon";
import {
  MIN_OPTIONS_FOR_SEARCH,
  RowButton,
  SearchableDropdown,
  SectionLabel,
} from "../../SearchableDropdown";
import { Text } from "../../Text";
import { DropdownOpener } from "../shared";

export function RouteSpacePicker({
  value,
  onChange,
  disabled,
  excludePersonal = false,
  groupSpaces = false,
  allSpacesLabel = "All spaces",
}: {
  value: string | null;
  onChange?: (spaceId: string | null) => void;
  disabled?: boolean;
  excludePersonal?: boolean;
  groupSpaces?: boolean;
  allSpacesLabel?: string;
}) {
  const [spaces] = useQuery(queries.spacesLite({ includeAdminSpaces: true }));
  const [personalSpaces] = useQuery(queries.personalSpaceOwners());
  const owners = new Map(
    personalSpaces.map((space) => [space.id, space.roleGrantsAsScope[0]?.user])
  );
  const options = [
    {
      id: null,
      label: allSpacesLabel,
      icon: "layers" as const,
      searchText: allSpacesLabel,
    },
    ...spaces
      .filter((space) => !excludePersonal || space.mode !== "personal")
      .map((space) => {
        const owner = owners.get(space.id);
        const label = displaySpaceName(
          space,
          null,
          owner ? userDisplayName(owner) : undefined
        );
        return {
          id: space.id,
          label,
          icon: spaceIcon(space.mode),
          searchText: `${label} ${owner?.email ?? ""}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];
  const label =
    options.find((option) => option.id === value)?.label ?? "Selected space";
  if (!onChange) return <Text variant="text-small">{label}</Text>;
  return (
    <SearchableDropdown
      disabled={disabled}
      style={{ width: "100%", minWidth: 0 }}
      opener={
        <DropdownOpener as="span" style={{ minWidth: 0, gap: 8 }}>
          <Text variant="text-regular" ellipsis>
            {label}
          </Text>
          <Icon name="chevron-down" size={12} style={{ flexShrink: 0 }} />
        </DropdownOpener>
      }
      align="left"
      searchable={options.length >= MIN_OPTIONS_FOR_SEARCH}
      searchPlaceholder="Search spaces…"
    >
      {(query, close) => {
        const matches = options.filter((option) =>
          option.searchText.toLowerCase().includes(query.toLowerCase())
        );
        return matches.length > 0 ? (
          matches.map((option, index) => (
            <Fragment key={option.id ?? "all"}>
              {groupSpaces &&
                option.id !== null &&
                (index === 0 || matches[index - 1]?.id === null) && (
                  <SectionLabel>
                    <Text variant="text-small" color="var(--text-secondary)">
                      Spaces
                    </Text>
                  </SectionLabel>
                )}
              <RowButton
                onClick={() => {
                  onChange(option.id);
                  close();
                }}
              >
                <Icon name={option.icon} size={16} />
                <Text variant="text-regular" ellipsis>
                  {option.label}
                </Text>
              </RowButton>
            </Fragment>
          ))
        ) : (
          <Text variant="text-small">No matching spaces</Text>
        );
      }}
    </SearchableDropdown>
  );
}
